export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2.0;
export const ZOOM_STEP = 0.25;

export interface VisiblePageMeasurement {
  pageNumber: number;
  offsetTop: number;
  height: number;
}

export interface CanvasRenderMetrics {
  canvasWidth: number;
  canvasHeight: number;
  cssWidth: string;
  cssHeight: string;
  transform: [number, number, number, number, number, number] | null;
}

export interface PageHorizontalInsetInput {
  paddingLeft?: string;
  paddingRight?: string;
  borderLeftWidth?: string;
  borderRightWidth?: string;
  marginLeft?: string;
  marginRight?: string;
}

function parseNonAutoCssLength(value?: string) {
  if (!value || value === "auto") {
    return 0;
  }

  return Number.parseFloat(value) || 0;
}

export function normalizeViewerMode(mode: "pagination" | "scroll" | "continuous") {
  return mode === "continuous" ? "scroll" : mode;
}

export function getPageHorizontalInset({
  paddingLeft,
  paddingRight,
  borderLeftWidth,
  borderRightWidth,
}: PageHorizontalInsetInput) {
  return (
    parseNonAutoCssLength(paddingLeft) +
    parseNonAutoCssLength(paddingRight) +
    parseNonAutoCssLength(borderLeftWidth) +
    parseNonAutoCssLength(borderRightWidth)
  );
}

export function getCanvasRenderMetrics({
  viewportWidth,
  viewportHeight,
  outputScale,
}: {
  viewportWidth: number;
  viewportHeight: number;
  outputScale: number;
}): CanvasRenderMetrics {
  return {
    canvasWidth: Math.floor(viewportWidth * outputScale),
    canvasHeight: Math.floor(viewportHeight * outputScale),
    cssWidth: `${Math.floor(viewportWidth)}px`,
    cssHeight: `${Math.floor(viewportHeight)}px`,
    transform:
      outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
  };
}

export function getFitContainerZoom({
  containerWidth,
  pageWidth,
  defaultZoom,
  pageHorizontalInset = 0,
}: {
  containerWidth: number;
  pageWidth: number;
  defaultZoom: number;
  pageHorizontalInset?: number;
}) {
  const availableWidth = containerWidth - pageHorizontalInset;

  if (!availableWidth || availableWidth < 0 || !pageWidth) {
    return defaultZoom;
  }

  return (
    Math.round(clampZoom((availableWidth / pageWidth) * defaultZoom) * 1000) /
    1000
  );
}

export function clampZoom(zoom: number) {
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
}

export function normalizePageNumber(requestedPage: number, totalPages: number) {
  if (!totalPages || totalPages < 1) {
    return 1;
  }

  return Math.min(Math.max(Math.trunc(requestedPage) || 1, 1), totalPages);
}

export function getMostVisiblePage({
  scrollTop,
  containerHeight,
  fallbackPage,
  pages,
}: {
  scrollTop: number;
  containerHeight: number;
  fallbackPage: number;
  pages: VisiblePageMeasurement[];
}) {
  const containerBottom = scrollTop + containerHeight;
  let nextPage = fallbackPage;
  let maxVisibleHeight = 0;

  for (const page of pages) {
    const pageTop = page.offsetTop;
    const pageBottom = page.offsetTop + page.height;
    const visibleTop = Math.max(scrollTop, pageTop);
    const visibleBottom = Math.min(containerBottom, pageBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    if (visibleHeight > maxVisibleHeight) {
      maxVisibleHeight = visibleHeight;
      nextPage = page.pageNumber;
    }
  }

  return nextPage;
}
